//Holistic Frontend Analytics Wrapper
// Batches events to /analytics/ingest using new expanded taxonomy.
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

class HolisticAnalytics {
  constructor() {
    this.sessionId = this.loadOrCreate('ba_session_id');
    this.anonId = this.loadOrCreate('ba_anon_id');
    this.queue = [];
    this.timer = null;
    this.flushInterval = 15000;
    this.maxBatch = 25;
    this.started = Date.now();
    this.startFlushLoop();
  this.initGlobalErrorHandlers();
  this.initPerformanceObservers();
  // Mark session start
  this.track('session_start', { eventData: { startedAt: new Date().toISOString() }, featureUsed: 'new_session' });
  }

  loadOrCreate(key) {
    let v = localStorage.getItem(key);
    if (!v) { v = uuidv4(); localStorage.setItem(key, v); }
    return v;
  }

  context(extra = {}) {
    return {
      sessionId: this.sessionId,
      anonId: this.anonId,
      url: window.location.href,
      ts: new Date().toISOString(),
      screen: { w: window.screen.width, h: window.screen.height },
      ua: navigator.userAgent,
      ...extra
    };
  }

  track(eventType, payload = {}) {
    const baseEventData = { url: window.location.pathname, ...(payload.eventData || {}) };
    this.queue.push({ eventType, payload: { ...payload, eventData: baseEventData, featureUsed: payload.featureUsed } });
    if (this.queue.length >= this.maxBatch) this.flush();
  }

  trackClick(action, extra = {}) {
    this.track('ui.feature_used', { featureUsed: 'service_redirect', eventData: { action, ...extra } });
  }

  trackPageView(path, extra = {}) {
    this.track('ui.feature_discovered', { featureUsed: 'service_redirect', eventData: { page: path, ...extra } });
  }

  initGlobalErrorHandlers() {
    if (this._errorsHooked) return; // idempotent
    this._errorsHooked = true;
    window.addEventListener('error', (e) => {
      try {
        this.track('error_occurred', {
          featureUsed: 'service_redirect',
          eventData: {
            source: 'frontend',
            errorType: 'runtime_error',
            message: e.message,
            filename: e.filename,
            lineno: e.lineno,
            colno: e.colno
          },
          errorMessage: e.message
        });
      } catch(_){}
    });
    window.addEventListener('unhandledrejection', (e) => {
      try {
        this.track('error_occurred', {
          featureUsed: 'service_redirect',
          eventData: {
            source: 'frontend',
            errorType: 'unhandled_promise',
            reason: (e.reason && (e.reason.message || e.reason.toString())) || 'unknown'
          },
          errorMessage: (e.reason && (e.reason.message || e.reason.toString())) || 'unknown'
        });
      } catch(_){}
    });
  }

  initPerformanceObservers() {
    this.perf = { lcp: null, cls: 0, entries: [] };
    if ('PerformanceObserver' in window) {
      try {
        const lcpObserver = new PerformanceObserver((entryList) => {
          const entries = entryList.getEntries();
            const last = entries[entries.length - 1];
            if (last) this.perf.lcp = last.renderTime || last.loadTime || last.startTime;
        });
        lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
      } catch(_){}
      try {
        let clsValue = 0;
        const clsObserver = new PerformanceObserver((entryList) => {
          for (const entry of entryList.getEntries()) {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
            }
          }
          this.perf.cls = clsValue;
        });
        clsObserver.observe({ type: 'layout-shift', buffered: true });
      } catch(_){}
    }
    window.addEventListener('load', () => {
      setTimeout(() => this.capturePerformanceSnapshot(), 3000); // allow late paints
    });
  }

  capturePerformanceSnapshot() {
    try {
      const nav = performance.getEntriesByType('navigation')[0];
      const timing = nav || performance.timing;
      const metrics = {
        ttfb: nav ? nav.responseStart : timing.responseStart - timing.requestStart,
        domContentLoaded: nav ? nav.domContentLoadedEventEnd - nav.startTime : timing.domContentLoadedEventEnd - timing.navigationStart,
        loadTime: nav ? nav.loadEventEnd - nav.startTime : timing.loadEventEnd - timing.navigationStart,
        lcp: this.perf.lcp,
        cls: this.perf.cls
      };
      this.track('analytics.snapshot_generated', { featureUsed: 'service_redirect', eventData: { metrics } });
    } catch(_){}
  }

  async flush() {
    if (!this.queue.length) return;
    const batch = this.queue.splice(0, this.maxBatch);
    try {
      const headers = { 'Content-Type': 'application/json', 'x-session-id': this.sessionId };
      const token = localStorage.getItem('token');
      if (token) headers['x-access-token'] = token;
      await axios.post('/analytics/ingest', { events: batch }, { headers });
    } catch (e) {
      // Requeue a small subset on failure
      this.queue = [...batch.slice(0, 5), ...this.queue];
    }
  }

  startFlushLoop() {
    this.timer = setInterval(() => this.flush(), this.flushInterval);
    window.addEventListener('beforeunload', () => { this.flush(); });
    document.addEventListener('visibilitychange', () => { if (document.hidden) this.flush(); });
  }
}

const holisticAnalytics = new HolisticAnalytics();
export default holisticAnalytics;
